import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/axios";
import { WA_UNREAD_EVENT } from "../hooks/useWaUnread";
import "./WhatsappPage.css";

interface Conversation {
  phone: string;
  lastText: string | null;
  lastDirection: "in" | "out";
  lastAt: string;
  unread: number;
  lastInboundAt: string | null;
  profileName: string | null;
  clientId: number | null;
  clientName: string | null;
}

interface Msg {
  id: number;
  direction: "in" | "out";
  type: string;
  text: string | null;
  template: string | null;
  status: string;
  mediaUrl: string | null;
  createdAt: string;
}

interface Info {
  webhookUrl: string;
  inbound: number;
  lastInboundAt: string | null;
}

const WINDOW_MS = 24 * 3600 * 1000;
const TZ = "Asia/Almaty";

const fmtPhone = (p: string) =>
  p.length === 11 ? `+${p[0]} ${p.slice(1, 4)} ${p.slice(4, 7)} ${p.slice(7, 9)} ${p.slice(9)}` : `+${p}`;

const fmtTime = (iso: string) => {
  const d = new Date(iso);
  const hm = d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit", timeZone: TZ });
  if (d.toDateString() === new Date().toDateString()) return hm;
  return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", timeZone: TZ }) + " " + hm;
};

const hoursLeft = (lastInboundAt: string | null): number | null => {
  if (!lastInboundAt) return null;
  const left = new Date(lastInboundAt).getTime() + WINDOW_MS - Date.now();
  return left > 0 ? Math.ceil(left / 3600000) : null;
};

export default function WhatsappPage() {
  const [convs, setConvs] = useState<Conversation[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<Info | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const loadConvs = useCallback(async () => {
    try {
      const r = await api.get<Conversation[]>("/whatsapp/conversations");
      setConvs(r.data);
    } catch {}
  }, []);

  const loadMsgs = useCallback(async (phone: string) => {
    try {
      const r = await api.get<Msg[]>("/whatsapp/messages", { params: { phone } });
      setMsgs(r.data);
    } catch {}
  }, []);

  const markRead = useCallback(async (phone: string) => {
    try {
      await api.post("/whatsapp/read", { phone });
      window.dispatchEvent(new Event(WA_UNREAD_EVENT));
    } catch {}
  }, []);

  useEffect(() => {
    loadConvs();
    api.get<Info>("/whatsapp/info").then(r => setInfo(r.data)).catch(() => {});
    const t = setInterval(loadConvs, 10000);
    return () => clearInterval(t);
  }, [loadConvs]);

  useEffect(() => {
    if (!active) return;
    setMsgs([]);
    setErr("");
    loadMsgs(active).then(() => markRead(active));
    const t = setInterval(() => loadMsgs(active), 5000);
    return () => clearInterval(t);
  }, [active, loadMsgs, markRead]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [msgs.length, active]);

  // пока чат открыт, новые входящие сразу считаем прочитанными
  const lastId = msgs.length ? msgs[msgs.length - 1].id : 0;
  useEffect(() => {
    if (active && msgs.length && msgs[msgs.length - 1].direction === "in") {
      markRead(active).then(loadConvs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastId]);

  const conv = convs.find(c => c.phone === active) || null;
  const left = hoursLeft(conv?.lastInboundAt ?? null);

  // курсор сразу в поле ввода при открытии диалога
  useEffect(() => {
    if (active && left) inputRef.current?.focus();
  }, [active, left]);

  // поле растёт вместе с текстом
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 160) + "px";
  }, [text]);

  const send = async () => {
    if (!active || !text.trim() || busy) return;
    setBusy(true);
    setErr("");
    try {
      await api.post("/whatsapp/send", { phone: active, text });
      setText("");
      await loadMsgs(active);
      loadConvs();
      inputRef.current?.focus();
    } catch (e: any) {
      setErr(e?.response?.data?.message || "Не удалось отправить");
    } finally {
      setBusy(false);
    }
  };

  const avatarStyle = (phone: string) => {
    let h = 0;
    for (let i = 0; i < phone.length; i++) h = (h * 31 + phone.charCodeAt(i)) % 360;
    return { background: `hsla(${h}, 55%, 50%, 0.2)`, color: `hsl(${h}, 70%, 62%)` };
  };
  const dayKey = (iso: string) => new Date(iso).toLocaleDateString("ru-RU", { timeZone: TZ });
  const dayLabel = (iso: string) => {
    const d = new Date(iso);
    const today = new Date();
    const yest = new Date(Date.now() - 86400000);
    if (d.toDateString() === today.toDateString()) return "Сегодня";
    if (d.toDateString() === yest.toDateString()) return "Вчера";
    return d.toLocaleDateString("ru-RU", { day: "numeric", month: "long", timeZone: TZ });
  };

  const name = (c: Conversation) => c.clientName || c.profileName || fmtPhone(c.phone);
  const initial = (c: Conversation) => (name(c).replace(/^[^A-Za-zА-Яа-яЁёӘәҒғҚқҢңӨөҰұҮүҺһІі0-9]+/, "")[0] || "#").toUpperCase();

  return (
    <div className="wa-page">
      <div className="wa-head">
        <h2>WhatsApp</h2>
        <button className="wa-link-btn" onClick={() => setShowInfo(s => !s)}>
          Настройка приёма
        </button>
      </div>

      {showInfo && info && (
        <div className="wa-info">
          <div>Этот адрес указан у KazInfoTeh как webhook входящих сообщений:</div>
          <code>{info.webhookUrl}</code>
          <div className="wa-muted">
            Принято входящих: {info.inbound}
            {info.lastInboundAt ? `, последнее: ${fmtTime(info.lastInboundAt)}` : ""}. Адрес содержит секрет, никому его не передавайте.
          </div>
        </div>
      )}

      <div className={`wa-body ${active ? "chat-open" : ""}`}>
        {/* Список диалогов */}
        <div className="wa-list">
          {convs.length === 0 && (
            <div className="wa-empty">Диалогов пока нет. Здесь появятся сообщения клиентов и ваши отправленные шаблоны.</div>
          )}
          {convs.map(c => (
            <div key={c.phone} className={`wa-conv ${c.phone === active ? "active" : ""}`} onClick={() => setActive(c.phone)}>
              <div className="wa-avatar" style={avatarStyle(c.phone)}>{initial(c)}</div>
              <div className="wa-conv-main">
                <div className="wa-row">
                  <span className="wa-name">{name(c)}</span>
                  <span className="wa-time">{fmtTime(c.lastAt)}</span>
                </div>
                <div className="wa-row">
                  <span className="wa-last">
                    {c.lastDirection === "out" ? "Вы: " : ""}
                    {c.lastText || "…"}
                  </span>
                  {c.unread > 0 && <span className="wa-badge">{c.unread}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Переписка */}
        <div className="wa-chat">
          {!conv ? (
            <div className="wa-placeholder">Выберите диалог слева</div>
          ) : (
            <>
              <div className="wa-chat-head">
                <button className="wa-back" onClick={() => setActive(null)} aria-label="Назад">
                  ←
                </button>
                <div className="wa-avatar" style={avatarStyle(conv.phone)}>{initial(conv)}</div>
                <div className="wa-chat-title">
                  <b>{name(conv)}</b>
                  <span>{fmtPhone(conv.phone)}</span>
                </div>
                {conv.clientId && <Link to={`/clients/${conv.clientId}`}>Карточка клиента</Link>}
                <span className={`wa-window ${left ? "open" : "closed"}`}>
                  {left ? `Можно отвечать ещё ~${left} ч` : "Окно 24 ч закрыто: только шаблон"}
                </span>
              </div>

              <div className="wa-msgs">
                {msgs.map((m, i) => (
                  <Fragment key={m.id}>
                    {(i === 0 || dayKey(m.createdAt) !== dayKey(msgs[i - 1].createdAt)) && (
                      <div className="wa-day">
                        <span>{dayLabel(m.createdAt)}</span>
                      </div>
                    )}
                  <div className={`wa-msg ${m.direction}`}>
                    {m.mediaUrl && m.type === "image" && (
                      <a href={m.mediaUrl} target="_blank" rel="noreferrer">
                        <img src={m.mediaUrl} alt="" />
                      </a>
                    )}
                    {m.mediaUrl && m.type !== "image" && (
                      <div>
                        <a href={m.mediaUrl} target="_blank" rel="noreferrer">
                          Открыть вложение ({m.type})
                        </a>
                      </div>
                    )}
                    {m.text || (m.mediaUrl ? "" : `[${m.type}]`)}
                    <div className="wa-meta">
                      {fmtTime(m.createdAt)}
                      {m.direction === "out" && m.status === "sent" && " ✓"}
                      {m.direction === "out" && m.status === "delivered" && " ✓✓"}
                      {m.direction === "out" && m.status === "read" && <span className="wa-read"> ✓✓</span>}
                      {m.status === "failed" && <span className="wa-fail"> · не доставлено</span>}
                    </div>
                  </div>
                  </Fragment>
                ))}
                <div ref={endRef} />
              </div>

              <div className="wa-compose">
                {err && <div className="wa-error">{err}</div>}
                <div className="wa-compose-row">
                  <textarea
                    ref={inputRef}
                    className="wa-input"
                    value={text}
                    onChange={e => setText(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    disabled={!left}
                    placeholder={left ? "Напишите сообщение…" : "Окно 24 часа закрыто: ответить можно только шаблоном"}
                    rows={1}
                  />
                  <button className="wa-send" onClick={send} disabled={busy || !left || !text.trim()} title="Отправить" aria-label="Отправить">
                    ➤
                  </button>
                </div>
                {left && <div className="wa-hint">Enter отправляет, Shift+Enter перенос строки</div>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
